/**
 * Default: Components / Shared > Table
 *
 * @copyright 2024 i-fabrik GmbH
 * @author Heiko Pfefferkorn
 */

import {isElement} from '@ifabnpm/bootstrap/utils/is.js';

import SelectorEngine from '@ifabnpm/bootstrap/dom/selector-engine.js';
import Data from '@ifabnpm/bootstrap/dom/data.js';
import Manipulator from '@ifabnpm/bootstrap/dom/manipulator.js';

// -------
// Private
// -------

const NAME     = 'table';
const DATA_KEY = `ifab.${NAME}`;
// const EVENT_KEY  = `.${DATA_KEY}`;
// const API_KEY    = '.data-api';

/**
 * Tabelle scrollbar
 *
 * @param {HTMLElement} table
 * @returns {Promise<void>}
 */
const makeScrollable = async (table) => {
	if (!Data.get(table, `${DATA_KEY}.scrollable`)) {
		let figureWrap = SelectorEngine.parents(table, 'figure')[0];

		if (figureWrap) {
			Manipulator.addClass(table, 'table');
		} else {
			// `<figure/>` gernerieren und nach der Tabelle einfügen.
			figureWrap = Manipulator.elementAfter('<figure class="table-container"/>', table);

			// Tabelle in `<figure/>` verschieben.
			Manipulator.elementAppend(figureWrap, table);
		}

		Manipulator.removeClass(figureWrap, 'table');
		Manipulator.addClass(figureWrap, 'table-container');

		figureWrap.style.overflowX = 'auto';

		// Initialisierungsstatus setzen.
		Data.set(table, `${DATA_KEY}.scrollable`, true);
	}
};

/**
 * Zellen mit einem Styleattribut auf Vorhandensein der Eigenschaft `width` prüfen und entsprechend um die Eigenschaften
 * `max-width` und `min-width` erweitern. Somit wird die Zellenbreite bzgl. responsivem Verhalten beibehalten.
 *
 * Dies gilt aber nur für Breitenangaben die nicht mit `%` gesetzt wurden!
 *
 * @param {HTMLElement} table
 * @returns {Promise<void>}
 */
const checkCellWidths = async (table) => {
	const cellsWithStyle = SelectorEngine.find('th[style], td[style]', table);

	for (const cell of cellsWithStyle) {
		if (!Data.get(cell, `${DATA_KEY}.checkcellwidth`)) {
			if (undefined !== cell.attributeStyleMap) {
				const width = cell.attributeStyleMap.get('width');

				if (width.value && width.unit && width.unit !== '%') {
					cell.style.maxWidth = `${width.value}${width.unit}`;
					cell.style.minWidth = `${width.value}${width.unit}`;
				}
			} else if (undefined !== cell.style.width && cell.style.width.indexOf('%') === -1) {
					cell.style.maxWidth = `${cell.style.width}`;
					cell.style.minWidth = `${cell.style.width}`;
			}

			// Initialisierungsstatus setzen.
			Data.set(table, `${DATA_KEY}.checkcellwidth`, true);
		}
	}
};

// ------
// Public
// ------

/**
 * @returns {Promise<void>}
 */
const init = async (o = {}) => {
	const collection = SelectorEngine.find('table');

	for (const table of collection) {
		await makeScrollable(table);
		await checkCellWidths(table);
	}
};

// Export
export default {
	init: init
};
